import { closeModal } from "app/slices/modalSlice";

import { useTranslation } from "react-i18next";
import { Button, ControlledSelectGray } from "components/Shared";
import { useDispatch } from "react-redux";
import { IOnboardingDraft } from "interfaces";

import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";
import { useCreateOnboardingMutation } from "api/institutional/onboarding_api";

import { Send } from "lucide-react";
import useErrorListener from "hooks/useErrorListener";

import { emailPattern } from "utils/numberUtils";
import { ClientType } from "interfaces/enums";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface SendEmailKYB extends IOnboardingDraft {
  language: "en" | "es";
}

export const SendKYBModal = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { isValid },
  } = useForm<SendEmailKYB>({
    mode: "all",
    defaultValues: {
      full_name: "",
      company_name: "",
    },
  });

  const [mutate, { error, isLoading }] = useCreateOnboardingMutation();

  useErrorListener({ error });

  const clientType = watch("client_type");
  const isIndividual =
    clientType === ClientType.INDIVIDUAL_WITH_BUSINESS_ACTIVITY;

  const doSubmit = (values: SendEmailKYB) => {
    mutate({
      email: values.email?.trim(),
      company_name: values.company_name?.trim() || "",
      client_type: values.client_type,
      language: values.language,
      full_name: values.full_name?.trim() || "",
    } as any);
    toast.success(t("New KYB sent"));
    handleClose();
  };

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t("Send new KYB/KYC")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(doSubmit)} className="space-y-4">
        <span className="text-sm font-bold text-[#767373]">
          {t("Add the email of the user that will fill the KYB")}
        </span>
        <Controller
          name="client_type"
          rules={{
            required: true,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a Client Type")}
              renderProps={renderProps}
              options={[
                ClientType.CORPORATE_ENTITY,
                ClientType.INDIVIDUAL_WITH_BUSINESS_ACTIVITY,
              ]}
            />
          )}
        />
        {isIndividual && (
          <Input
            placeholder={t("Full Name")}
            {...register("full_name", { required: true })}
          />
        )}
        <Input
          placeholder={
            isIndividual ? t("Company Name (Optional)") : t("Company Name")
          }
          {...register("company_name", { required: !isIndividual })}
        />
        <Input
          placeholder={t("Email")}
          {...register("email", { required: true, pattern: emailPattern })}
        />
        <Controller
          name="language"
          rules={{
            required: true,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a language")}
              renderProps={renderProps}
              options={["en", "es"]}
            />
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={!isValid || isLoading}
        >
          <Send />
          {t("Send")}
        </Button>
      </form>
    </DialogContent>
  );
};

export default SendKYBModal;
